import React, { useState, useEffect } from 'react';
import { ChartMini } from '../../components';
import { getAnalytics, AnalyticsData } from '../../services/analytics-service';

const pct = (num: number, den: number) =>
  den === 0 ? '0%' : `${Math.round((num / den) * 100)}%`;

const clipData = (arr: number[], period: string) => {
  if (period === '7 Days')  return arr.slice(-2);
  if (period === '30 Days') return arr.slice(-4);
  if (period === '90 Days') return arr.slice(-6);
  return arr;
};

const roleDot: Record<string, string> = {
  founder: 'bg-role-founder',
  mentor:  'bg-role-mentor',
  admin:   'bg-role-admin',
};

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30 Days');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const periods = ['7 Days', '30 Days', '90 Days', '1 Year'];

  useEffect(() => {
    getAnalytics()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="section-gap">
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="section-gap">
        <div className="p-4 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
          {error || 'Failed to load analytics.'}
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Total Users',        value: data.users.total.toLocaleString() },
    { label: 'Active Mentors',     value: data.users.mentors.toLocaleString() },
    { label: 'Course Completions', value: data.enrollments.completed.toLocaleString() },
    { label: 'Sessions Completed', value: data.sessions.completed.toLocaleString() },
    { label: 'Total Startups',     value: data.startups.total.toLocaleString() },
    { label: 'Published Courses',  value: data.courses.published.toLocaleString() },
  ];

  const topMetrics = [
    { label: 'Course Completion Rate',  value: pct(data.enrollments.completed, data.enrollments.total) },
    { label: 'Session Completion Rate', value: pct(data.sessions.completed, data.sessions.total) },
    { label: 'Submission Review Rate',  value: pct(data.submissions.reviewed, data.submissions.total) },
    { label: 'Pending Mentor Approvals', value: String(data.users.pendingMentors) },
  ];

  const userChart       = clipData(data.charts.monthlyUsers, selectedPeriod);
  const enrollmentChart = clipData(data.charts.monthlyEnrollments, selectedPeriod);
  const sessionChart    = clipData(data.charts.monthlySessions, selectedPeriod);

  const stageLabels: Array<{ key: keyof typeof data.startups.byStage; label: string }> = [
    { key: 'idea',      label: 'Idea' },
    { key: 'pre-seed',  label: 'Pre-Seed' },
    { key: 'seed',      label: 'Seed' },
    { key: 'series-a',  label: 'Series A' },
  ];

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-line">
        <div>
          <h1 className="text-3xl font-bold text-text">Analytics</h1>
          <p className="text-muted">Platform performance overview</p>
        </div>
        <div className="flex gap-6">
          {periods.map(p => (
            <button
              key={p}
              onClick={() => setSelectedPeriod(p)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                selectedPeriod === p
                  ? 'border-chart text-text'
                  : 'border-transparent text-muted hover:text-text'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-6 py-6 border-b border-line">
        {kpis.map((kpi, i) => (
          <div key={i} className="flex flex-col">
            <span className="text-2xl font-bold text-text">{kpi.value}</span>
            <span className="text-xs text-muted mt-0.5">{kpi.label}</span>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="two-col">
        <section>
          <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">User Growth</p>
          <div className="h-48">
            <ChartMini data={userChart} width={400} height={180} />
          </div>
          <p className="mt-3 text-center text-muted text-xs">New user registrations over time</p>
        </section>

        <section>
          <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Course Enrollments</p>
          <div className="h-48">
            <ChartMini data={enrollmentChart} width={400} height={180} />
          </div>
          <p className="mt-3 text-center text-muted text-xs">New enrollments over time</p>
        </section>
      </div>

      {/* Second charts row */}
      <div className="two-col">
        <section>
          <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Mentoring Sessions</p>
          <div className="h-48">
            <ChartMini data={sessionChart} width={400} height={180} />
          </div>
          <p className="mt-3 text-center text-muted text-xs">Completed mentoring sessions</p>
        </section>

        <section>
          <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Top Performing Metrics</p>
          <div>
            {topMetrics.map((m, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-line last:border-0">
                <span className="text-text text-sm">{m.label}</span>
                <span className="text-chart font-medium text-sm">{m.value}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Startup Stage Breakdown */}
      <section>
        <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Startup Stage Breakdown</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stageLabels.map(({ key, label }) => (
            <div key={key} className="bg-surface border border-line rounded-card p-4 text-center">
              <div className="text-2xl font-bold text-text">{data.startups.byStage[key]}</div>
              <div className="text-muted text-sm mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Recent Activity */}
      <section>
        <p className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">Recent Users</p>
        <div>
          {data.recentUsers.length === 0 ? (
            <p className="text-muted text-sm">No users yet.</p>
          ) : (
            data.recentUsers.map((user) => (
              <div key={user._id} className="flex items-center gap-3 py-3 border-b border-line last:border-0">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${roleDot[user.role] || 'bg-muted'}`} />
                <div className="flex-1">
                  <div className="text-text text-sm">{user.name} joined as {user.role}</div>
                  <div className="text-muted text-xs">{new Date(user.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Analytics;
